// Framework and third-party non-ui
import React, { Component } from "react";

// Redux operations and local helpers/utils/modules
import { connect } from "react-redux";
import Rdx from "../../../redux/Rdx";
import Context from "../../../context/Context";
import { ModalController } from "../Modal";
import Topic from "../../../context/Topic";
import TransactionGuard from "../../base/TransactionGuard";
import * as component from "../../../components/util/component";
import * as officePlanUtil from "../../base/officePlanUtil";
import * as queryUtil from "../../base/queryUtil";
import * as transactions from "../../base/transaction/transactions";

// Component specific modules (Component-styled, etc.)
import { AssignToContainer } from "../../styles/Common/commonStyles";
import { ComboButton, Button } from "../../styles/Common/peopleAssignTo";

// App components
import OfficePlan from "../../base/OfficePlan";
import SelectArea from "./SelectArea";
import SelectUnit from "./SelectUnit";
import UnassignPrompt from "./UnassignPrompt";

// Third-party components (buttons, icons, etc.)
import Menu, { MenuItem } from "calcite-react/Menu";
import UnitsIcon from "calcite-ui-icons-react/FloorPlanIcon";
import HotDeskIcon from "calcite-ui-icons-react/DeskIcon";
import HotelIcon from "calcite-ui-icons-react/ServicesIcon";
import UnassignIcon from "calcite-ui-icons-react/MinusCircleIcon";
import ReactTooltip from "react-tooltip";

// JSON

// CSS

class PeopleAssignTo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isButtons: this.props.buttons !== undefined ? this.props.buttons : false,
      name: this.props.name || "",
    };
  }

  componentDidMount = () => {};

  componentDidUpdate = () => {
    ReactTooltip.rebuild();
  };

  componentWillUnmount = () => {
    component.componentWillUnmount(this);
  };

  assignPeopleToArea = (areaItem, areaType) => {
    const personFeatureItems = this.getFeatureItems();
    const guard = new TransactionGuard({featureItems: personFeatureItems});
    const allAssignedToArea = personFeatureItems > 1
    transactions
      .assignPeopleToArea(personFeatureItems, areaItem, areaType)
      .then((result) => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated,
          allAssignedToArea: allAssignedToArea
        });
        this.onAssignment();
      })
      .catch((error) => {
        guard.close();
        console.error("Error assigning people to area", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  assignPeopleToUnit = (unitFeatureItem) => {
    const personFeatureItems = this.getFeatureItems();
    const guard = new TransactionGuard({featureItems: personFeatureItems});
    transactions
      .assignPeopleToUnit(personFeatureItems, unitFeatureItem)
      .then((result) => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated,
        });
        this.onAssignment();
      })
      .catch((error) => {
        guard.close();
        console.error("Error assigning person to unit", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  assignToClicked = (evt, option) => {
    if (!this.canAssign(this.getFeatureItems())) return;
    const value = option.value;
    if (value === "assignToUnit") {
      this.assignToUnitClicked(option.caption, evt);
    } else if (value === "assignToHotDesk") {
      let areaType = OfficePlan.SpaceAssignmentTypes.hotdesk;
      this.assignToAreaClicked(option.caption, areaType, evt);
    } else if (value === "assignToHotel") {
      let areaType = OfficePlan.SpaceAssignmentTypes.hotel;
      this.assignToAreaClicked(option.caption, areaType, evt);
    } else if (value === "assignToNone") {
      this.assignToNoneClicked(evt);
    }
  };

  assignToAreaClicked = (title, areaType, evt) => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems())) return;
    SelectArea.showModal({
      title: title,
      areaType: areaType,
      promptOptions: {
        subject: "people",
        subjectItems: this.getFeatureItems(),
        target: areaType,
      },
      onOK: (sel) => {
        const areaItem = sel;
        if (areaItem) {
          this.assignPeopleToArea(areaItem, areaType);
        }
      },
    });
  };

  assignToUnitClicked = (title, evt) => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems())) return;
    SelectUnit.showModal({
      title: title,
      returnGeometry: true,
      promptOptions: {
        subject: "people",
        subjectItems: this.getFeatureItems(),
        target: "unit",
      },
      onOK: (sel) => {
        let objectId = sel && sel.objectId;
        if (typeof objectId === "number") {
          const source = officePlanUtil.getUnitsSource();
          const featureItem = queryUtil.makeFeatureItem(source, sel.feature);
          this.assignPeopleToUnit(featureItem);
        }
      },
    });
  };

  assignToNoneClicked = evt => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems())) return;
    const i18n = Context.instance.i18n;
    let title = i18n.spaceplanner.unassignPrompt.removeAssignmentsTitle;
    const featureItems = this.getFeatureItems();
    let promptOptions = {
      subject: "people",
      subjectItems: featureItems,
    };
    if (featureItems.length === 1) {
      title = i18n.spaceplanner.unassignPrompt.removeAssignmentTitle;
      const featureItem = featureItems[0];
      if (!officePlanUtil.isPersonUnassigned(featureItem)) {
        const fromName = officePlanUtil.getPersonAssignmentName(featureItem);
        promptOptions = {
          subject: "person",
          subjectItem: featureItem,
          fromName: fromName,
        };
      }
    }
    const content = (
      <UnassignPrompt
        promptOptions={promptOptions}
        peopleFeatureItems={featureItems}
      />
    );
    const controller = new ModalController({});
    let okButton;
    let props = {
      title: title,
      showOKCancel: true,
      closeOnOK: true,
      okLabel: i18n.spaceplanner.unassign,
      onOK: () => {
        if (okButton) okButton.disable();
        this.unassignPeople();
      },
      onMountOKButton: (btn) => {
        okButton = btn;
      },
    };
    controller.show(props, content);
  };

  canAssign = (featureItems) => {
    return (
      featureItems &&
      featureItems.length > 0 &&
      featureItems.length <= this.getMaxFeatureItems()
    );
  };

  canUnassign = (featureItems) => {
    if (!featureItems || featureItems.length === 0) {
      return false;
    } else if (this.props.disableUnassign) {
      return false;
    } else if (this.props.enableUnassign) {
      return true;
    }
    for (let i = 0; i < featureItems.length; i++) {
      if (!officePlanUtil.isPersonUnassigned(featureItems[i])) {
        return true;
      }
    }
    return false;
  };

  getFeatureItems = () => {
    return this.props.featureItems;
  };

  getMaxFeatureItems = () => {
    return 1001;
  };

  onAssignment = () => {
    if (typeof this.props.onAssignment === "function") {
      this.props.onAssignment();
    }
  };

  renderButtons = () => {
    const i18n = Context.instance.i18n;
    const featureItems = this.getFeatureItems();
    const hasUnassignButton = this.canUnassign(featureItems);
    const captionKey =
      featureItems && featureItems.length > 1
        ? "captionPlural"
        : "captionSingular";
    return (
      <>
        <Button
          onClick={evt =>
            this.assignToUnitClicked(
              i18n.spaceplanner.people.assignTo.unit[captionKey],
              evt
            )
          }
          icon={<UnitsIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.people.assignTo.unit.label}
          data-for="genericTooltip"
        />
        <Button
          onClick={evt =>
            this.assignToAreaClicked(
              i18n.spaceplanner.people.assignTo.hotdesk[captionKey],
              OfficePlan.SpaceAssignmentTypes.hotdesk,
              evt
            )
          }
          icon={<HotDeskIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.people.assignTo.hotdesk.label}
          data-for="genericTooltip"
        />
        <Button
          onClick={evt =>
            this.assignToAreaClicked(
              i18n.spaceplanner.people.assignTo.hotel[captionKey],
              OfficePlan.SpaceAssignmentTypes.hotel,
              evt
            )
          }
          icon={<HotelIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.people.assignTo.hotel.label}
          data-for="genericTooltip"
        />
        {hasUnassignButton ? (
          <Button
            clear
            icon={<UnassignIcon size={16} />}
            iconPosition="before"
            onClick={evt => this.assignToNoneClicked(evt)}
            data-tip={i18n.spaceplanner.unassign}
            data-for="genericTooltip"
          />
        ) : null}
      </>
    );
  };

  renderCombo = () => {
    const i18n = Context.instance.i18n;
    const featureItems = this.getFeatureItems();
    const disabled = !this.canAssign(featureItems);
    const hasUnassignButton = this.canUnassign(featureItems);
    let captionKey = "captionSingular";
    if (featureItems && featureItems.length > 1) captionKey = "captionPlural";

    const options = [
      {
        label: i18n.spaceplanner.people.assignTo.unit.label,
        caption: i18n.spaceplanner.people.assignTo.unit[captionKey],
        value: "assignToUnit",
      },
      {
        label: i18n.spaceplanner.people.assignTo.hotdesk.label,
        caption: i18n.spaceplanner.people.assignTo.hotdesk[captionKey],
        value: "assignToHotDesk",
      },
      {
        label: i18n.spaceplanner.people.assignTo.hotel.label,
        caption: i18n.spaceplanner.people.assignTo.hotel[captionKey],
        value: "assignToHotel",
      },
    ];

    const menuItems = [];
    options.forEach((option) => {
      menuItems.push(
        <MenuItem
          key={option.value}
          onClick={evt => this.assignToClicked(evt, option)}
        >
          {option.label}
        </MenuItem>
      );
    });

    return (
      <AssignToContainer noWrap={this.props.noWrap}>
        <ComboButton
          title={i18n.spaceplanner.people.assignTo.label}
          label={i18n.spaceplanner.people.assignTo.label}
          disabled={disabled}
          onClick={evt =>
            this.assignToUnitClicked(
              i18n.spaceplanner.people.assignTo.unit[captionKey],
              evt
            )
          }
        >
          <Menu>{menuItems}</Menu>
        </ComboButton>

        {hasUnassignButton ? (
          <Button
            title={i18n.spaceplanner.unassign}
            clear
            disabled={disabled}
            noWrap={this.props.noWrap}
            onClick={evt => this.assignToNoneClicked(evt)}
          >
            {i18n.spaceplanner.unassign}
          </Button>
        ) : null}
      </AssignToContainer>
    );
  };

  render() {
    const { isButtons } = this.state;
    return isButtons ? this.renderButtons() : this.renderCombo();
  }

  unassignPeople = () => {
    const featureItems = this.getFeatureItems();
    if (!this.canAssign(featureItems)) return;
    const guard = new TransactionGuard({featureItems: featureItems});
    transactions
      .unassignPeople(featureItems)
      .then((result) => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated,
        });
        this.onAssignment();
      })
      .catch((error) => {
        guard.close();
        console.error("Error unassigning people", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };
}

const mapStateToProps = (state, ownProps) => {
  const rdxFeatureItemsKey = ownProps.rdxFeatureItemsKey;
  if (rdxFeatureItemsKey && !ownProps.featureItems) {
    return { featureItems: Rdx.getValue(state, rdxFeatureItemsKey) };
  } else {
    return {};
  }
};

export default connect(mapStateToProps)(PeopleAssignTo);
